import type {NormalizedCacheObject} from '@apollo/client';
import {
    type ApolloClientProxy,
    type BaseHttpCodes,
    EntityBaseGqlService,
    type GetDownloadLinkRequestPayload,
    type IDownloadByLinkRequestBuilder,
    type IDownloadByLinkService,
    type ServiceResponsePayload,
} from '@igp/shared/api';
import type {
    AgentPlayerReportingV2,
    Mutation,
    Query,
    QueryExportAgentPlayerReportingsV2Args,
    QueryGetAgentPlayerReportingsV2Args,
    Subscription,
} from '@igp/shared/contract/pam';
import type {IMessageLocalizer} from '@igp/shared/localization';
import {map} from '@igp/shared/otel';
import type {EntityFetchRequestPayload, EntityFetchServiceResponsePayload} from '@igp/shared/redux/entity';
import type {EntityType} from '@models';
import {inject, injectable} from 'inversify';
import type {Observable} from 'rxjs';

import {ServiceTypes} from '@ioc-container';

import {PlayerLevelReportV2RequestBuilder} from './requestBuilder';
import {
    type PlayerLevelReportV2DownloadFields,
    type PlayerLevelReportV2QueryFilterKeys,
    type PlayerLevelReportV2QueryKeys,
    summaryId,
} from './types';

@injectable()
export class PlayerLevelReportV2Service
    extends EntityBaseGqlService<
        EntityType,
        Query,
        QueryGetAgentPlayerReportingsV2Args,
        PlayerLevelReportV2QueryKeys,
        Mutation,
        Subscription,
        BaseHttpCodes,
        PlayerLevelReportV2QueryFilterKeys
    >
    implements IDownloadByLinkService<PlayerLevelReportV2DownloadFields>
{
    protected readonly _requestBuilder: IDownloadByLinkRequestBuilder<
        QueryExportAgentPlayerReportingsV2Args,
        PlayerLevelReportV2DownloadFields
    >;
    constructor(
        @inject(ServiceTypes.ApolloClientIGP) client: ApolloClientProxy<NormalizedCacheObject>,
        @inject(ServiceTypes.MessageLocalizer) messageLocalizer: IMessageLocalizer
    ) {
        const builder = new PlayerLevelReportV2RequestBuilder();
        super(client, builder, messageLocalizer);
        this._requestBuilder = builder;
    }

    protected getErrorSeverity(_code: BaseHttpCodes): number {
        return 0;
    }

    get(
        requestPayload: EntityFetchRequestPayload<EntityType, PlayerLevelReportV2QueryKeys>
    ): Observable<EntityFetchServiceResponsePayload<EntityType, AgentPlayerReportingV2>> {
        return this.fetchData(requestPayload).pipe(
            map(({status, message, errors, responsePayload}) => {
                const summaryItem: AgentPlayerReportingV2 = responsePayload?.getAgentPlayerReportingsV2?.summary
                    ? {
                          ...responsePayload.getAgentPlayerReportingsV2.summary,
                          uid: summaryId,
                          __typename: 'AgentPlayerReportingV2',
                      }
                    : null;
                const items = summaryItem
                    ? [...(responsePayload?.getAgentPlayerReportingsV2?.items ?? []), summaryItem]
                    : (responsePayload?.getAgentPlayerReportingsV2?.items ?? []);
                return {
                    status,
                    message,
                    errors,
                    requestPayload,
                    responsePayload: {
                        items,
                        total: responsePayload?.getAgentPlayerReportingsV2?.total_count ?? 0,
                    },
                };
            })
        );
    }

    getDownloadLink(
        requestPayload: GetDownloadLinkRequestPayload<PlayerLevelReportV2DownloadFields>
    ): Observable<ServiceResponsePayload<GetDownloadLinkRequestPayload<PlayerLevelReportV2DownloadFields>, string>> {
        const request = this._requestBuilder.buildDownloadRequest(requestPayload);
        return this._service.query(request.query, request.variables).pipe(
            map(response => ({
                ...response,
                requestPayload,
                responsePayload: response?.responsePayload?.exportAgentPlayerReportingsV2?.url,
            }))
        );
    }
}
